import type { ApiClient, TypedFetchResponse } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { infiniteQueryOptions, type QueryKey } from "@tanstack/react-query";

export const statusHistoryPageSize = 20;

export interface StatusHistoryPage<T> {
    items: T[];
    /** Total event count from the `X-Paging-Total` response header (`undefined` if it is missing) */
    total: number | undefined;
}

export interface Params {
    billing_account_id: number;
}

function getPagingTotal(response: Response) {
    const total = Number(response.headers.get("X-Paging-Total") ?? NaN);
    return Number.isInteger(total) ? total : undefined;
}

function getNextPageParam<T>(lastPage: StatusHistoryPage<T>, allPages: StatusHistoryPage<T>[], lastPageParam: number) {
    const loadedCount = allPages.reduce((count, page) => count + page.items.length, 0);
    const isLastPage =
        lastPage.items.length < statusHistoryPageSize ||
        (lastPage.total !== undefined && loadedCount >= lastPage.total);
    return isLastPage ? undefined : lastPageParam + 1;
}

function getQuery<T>(
    queryKey: QueryKey,
    fetchPage: (page: number, signal: AbortSignal) => Promise<TypedFetchResponse<T[]>>,
) {
    return infiniteQueryOptions({
        queryKey,
        queryFn: async ({ pageParam, signal }): Promise<StatusHistoryPage<T>> => {
            const fetchResponse = await fetchPage(pageParam, signal);
            return { items: getResponseData(fetchResponse), total: getPagingTotal(fetchResponse.response) };
        },
        initialPageParam: 1,
        getNextPageParam,
    });
}

export function baStatusHistoryQuery(client: ApiClient, { billing_account_id }: Params) {
    return getQuery(
        ["/payment/billing_account/status_history", billing_account_id],
        async (page, signal) =>
            await client.GET("/payment/billing_account/status_history", {
                signal,
                params: { query: { billing_account_id, limit: statusHistoryPageSize, page } },
            }),
    );
}

export function adminBaStatusHistoryQuery(client: ApiClient, { billing_account_id }: Params) {
    return getQuery(
        ["/payment/admin/billing_account/status_history", billing_account_id],
        async (page, signal) =>
            await client.GET("/payment/admin/billing_account/status_history", {
                signal,
                params: { query: { billing_account_id, limit: statusHistoryPageSize, page } },
            }),
    );
}
